// Polyfill Uint8Array.toHex — pdfjs-dist v5 uses it but Chromium < 140 lacks it
if (!(Uint8Array.prototype as any).toHex) {
  (Uint8Array.prototype as any).toHex = function () {
    return Array.from(this as Uint8Array, (b: number) => b.toString(16).padStart(2, '0')).join('');
  };
}

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import './styles/index.css';

const _urlParams = new URLSearchParams(window.location.search);
const _sldStandaloneKey = _urlParams.get('sld-standalone');

if (_urlParams.get('sld-portal') === '1') {
  // ── Browser SLD Portal mode ──────────────────────────────────────
  // Opened as a popup by SLDNewWindowPortal in browser (not Electron).
  // Renders only a portal root div — parent window uses createPortal().
  import('./components/monitoring/SLDViewerModal.css');
  const root = document.getElementById('root')!;
  root.id = 'sld-portal-root';
  root.style.width = '100vw';
  root.style.height = '100vh';
  document.body.style.margin = '0';
  document.body.style.overflow = 'hidden';
} else if (_sldStandaloneKey) {
  // ── Electron SLD Standalone mode ─────────────────────────────────
  // Opened as a separate BrowserWindow via IPC. Runs its own React tree
  // with monitoring state hydrated from localStorage (since Electron's
  // WindowProxy doesn't allow cross-window DOM access for createPortal).
  (async () => {
    await import('./components/monitoring/SLDViewerModal.css');
    const { SLDStandaloneViewer } = await import('./components/monitoring/SLDViewerModal');
    const { useMonitoringStore } = await import('./stores/monitoringStore');

    const stateJson = localStorage.getItem(_sldStandaloneKey);
    if (!stateJson) {
      document.getElementById('root')!.textContent = 'SLD state not found';
      return;
    }
    localStorage.removeItem(_sldStandaloneKey);

    const state = JSON.parse(stateJson);

    // Hydrate monitoring store so SLDStandaloneViewer reads alarm states + schema
    useMonitoringStore.setState({
      runtimeStates: new Map(Object.entries(state.runtimeStates || {})),
      triggeredAdvancedRules: state.triggeredRules || [],
      projectConfig: state.projectConfig || null,
      schema: state.schema || null,
    });

    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <SLDStandaloneViewer diagram={state.diagram} />
      </StrictMode>,
    );
  })();
} else {
  // ── Normal app rendering ─────────────────────────────────────────
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: 1,
      },
    },
  });

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>,
  );
}
